import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { EmployeeRow } from '../lib/database.types';
import { setErrorUser, clearErrorUser, captureMutationError } from '../utils/errorReporting';

interface AuthContextProps {
  user: User | null;
  session: Session | null;
  employeeProfile: EmployeeRow | null;
  claimableMatch: EmployeeRow | null;
  hasUnclaimedProfiles: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  claimProfile: (employeeId: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [employeeProfile, setEmployeeProfile] = useState<EmployeeRow | null>(null);
  const [claimableMatch, setClaimableMatch] = useState<EmployeeRow | null>(null);
  const [hasUnclaimedProfiles, setHasUnclaimedProfiles] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadProfileAndRoles = async (userId: string, userEmail?: string) => {
    const [profileRes, rolesRes, unclaimedCountRes] = await Promise.all([
      supabase.from('employees').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('user_roles').select('role').eq('user_id', userId),
      supabase.from('employees').select('id', { count: 'exact', head: true }).is('user_id', null),
    ]);

    const profile = profileRes.data ?? null;
    setEmployeeProfile(profile);
    setIsAdmin((rolesRes.data ?? []).some(r => r.role === 'admin' || r.role === 'hr'));

    const unclaimedCount = unclaimedCountRes.count ?? 0;
    setHasUnclaimedProfiles(unclaimedCount > 0);

    // Only look for a claimable match if no profile is linked yet
    if (!profile && userEmail && unclaimedCount > 0) {
      const { data: match } = await supabase
        .from('employees')
        .select('*')
        .eq('email', userEmail)
        .is('user_id', null)
        .maybeSingle();
      setClaimableMatch(match ?? null);
    } else {
      setClaimableMatch(null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setErrorUser(session.user.id);
        loadProfileAndRoles(session.user.id, session.user.email).finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setErrorUser(session.user.id);
        (async () => {
          await loadProfileAndRoles(session.user.id, session.user.email);
        })();
      } else {
        clearErrorUser();
        setEmployeeProfile(null);
        setClaimableMatch(null);
        setHasUnclaimedProfiles(false);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    if (user) {
      await loadProfileAndRoles(user.id, user.email);
    }
  };

  const claimProfile = async (employeeId: string) => {
    if (!user) return { error: 'Not signed in' };
    const { error } = await supabase
      .from('employees')
      .update({ user_id: user.id })
      .eq('id', employeeId)
      .is('user_id', null);
    if (error) {
      captureMutationError(error, { operation: 'claimProfile', table: 'employees', entityId: employeeId });
      return { error: error.message };
    }
    await refreshProfile();
    return { error: null };
  };

  return (
    <AuthContext.Provider value={{
      user, session, employeeProfile, claimableMatch, hasUnclaimedProfiles,
      isAdmin, isLoading,
      signInWithGoogle, signInWithPassword, signOut, refreshProfile, claimProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
